import { useEffect, useState, useMemo, useRef } from 'react';
import { svgIconCache, updateSvgFill, sanitizeSvg } from '@/utils/svgIconCache';
import { cn } from '@/lib/utils';

interface IconSvgRendererProps {
  iconId: string;
  storageUrl: string;
  color: string;
  className?: string;
  style?: React.CSSProperties;
  alt?: string;
}

/**
 * React komponenta pro renderování SVG ikon s možností změny barvy
 * Načte SVG jednou do cache a pak jen upravuje fill atribut
 */
export function IconSvgRenderer({
  iconId,
  storageUrl,
  color,
  className,
  style,
  alt,
}: IconSvgRendererProps) {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Načíst SVG obsah při mount nebo změně storageUrl
  useEffect(() => {
    let isMounted = true;

    const loadSvg = async () => {
      setLoading(true);
      setError(null);

      try {
        if (import.meta.env.DEV) {
          // console.log('[IconSvgRenderer] Loading SVG for icon:', iconId, 'from:', storageUrl);
        }

        const content = await svgIconCache.getSvg(storageUrl);
        if (isMounted) {
          if (content) {
            if (import.meta.env.DEV) {
              // console.log('[IconSvgRenderer] SVG loaded successfully for icon:', iconId, 'length:', content.length);
            }
            setSvgContent(content);
          } else {
            const errorMsg = `Failed to load SVG from ${storageUrl}`;
            if (import.meta.env.DEV) {
              console.error('[IconSvgRenderer]', errorMsg);
            }
            setError(errorMsg);
          }
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          const errorMsg = err instanceof Error ? err.message : 'Unknown error';
          console.error('[IconSvgRenderer] Error loading SVG:', errorMsg, err);
          setError(errorMsg);
          setLoading(false);
        }
      }
    };

    loadSvg();

    return () => {
      isMounted = false;
    };
  }, [storageUrl]);

  // Upravit SVG fill podle barvy - použít useMemo pro optimalizaci
  const coloredSvg = useMemo(() => {
    if (!svgContent || !color) {
      return svgContent;
    }
    return updateSvgFill(svgContent, color);
  }, [svgContent, color]);

  // Sanitizovat SVG před renderováním
  const sanitizedSvg = useMemo(() => {
    if (!coloredSvg) {
      return null;
    }
    return sanitizeSvg(coloredSvg);
  }, [coloredSvg]);

  // Vložit SVG do DOM pomocí DOMParser pro správné parsování
  useEffect(() => {
    if (!containerRef.current || !sanitizedSvg) {
      return;
    }

    // Vyčistit obsah kontejneru
    containerRef.current.innerHTML = '';

    try {
      if (import.meta.env.DEV) {
        // console.log('[IconSvgRenderer] Parsing SVG, length:', sanitizedSvg.length, 'first 100 chars:', sanitizedSvg.substring(0, 100));
      }

      // Použít innerHTML přímo - prohlížeče jsou tolerantnější k nevalidnímu XML než DOMParser
      // Toto funguje lépe pro SVG s drobnými XML chybami
      if (containerRef.current) {
        // Vytvořit dočasný div pro parsování pomocí innerHTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sanitizedSvg;
        const svgElement = tempDiv.querySelector('svg') as SVGSVGElement;

        if (svgElement) {
          // Aplikovat styly
          svgElement.style.width = '100%';
          svgElement.style.height = '100%';
          svgElement.style.display = 'block';

          // Přidat aria-label pro přístupnost
          if (alt) {
            svgElement.setAttribute('aria-label', alt);
          }

          // Vložit SVG do kontejneru
          containerRef.current.appendChild(svgElement);

          if (import.meta.env.DEV) {
            // console.log('[IconSvgRenderer] SVG successfully inserted using innerHTML for icon:', iconId);
          }
        } else {
          console.error('[IconSvgRenderer] No SVG element found in content');
          if (containerRef.current) {
            containerRef.current.innerHTML = '<span class="text-xs text-muted-foreground">?</span>';
          }
        }
      }
    } catch (err) {
      console.error('[IconSvgRenderer] Error inserting SVG:', err);
      if (containerRef.current) {
        containerRef.current.innerHTML = '<span class="text-xs text-muted-foreground">?</span>';
      }
    }
  }, [sanitizedSvg, alt]);

  // Renderovat loading state
  if (loading) {
    return (
      <div
        className={cn('flex items-center justify-center', className)}
        style={style}
        aria-label={alt || 'Loading icon'}
      >
        <div className="w-full h-full bg-muted animate-pulse rounded" />
      </div>
    );
  }

  // Renderovat error state pouze pokud je skutečně error
  if (error) {
    if (import.meta.env.DEV) {
      console.error('[IconSvgRenderer] Rendering error state for icon:', iconId, 'error:', error);
    }
    return (
      <div
        className={cn('flex items-center justify-center bg-muted', className)}
        style={style}
        aria-label={alt || 'Error loading icon'}
        title={error}
      >
        <span className="text-xs text-muted-foreground">?</span>
      </div>
    );
  }

  // Pokud nemáme sanitizedSvg, ale není error, stále renderovat kontejner
  // (může být ještě v procesu načítání nebo parsování)
  if (!sanitizedSvg) {
    if (import.meta.env.DEV) {
      console.warn('[IconSvgRenderer] No sanitized SVG but no error for icon:', iconId);
    }
    return (
      <div
        ref={containerRef}
        className={cn('inline-flex items-center justify-center', className)}
        style={style}
        aria-label={alt}
        role="img"
      />
    );
  }

  // Renderovat kontejner pro SVG (SVG se vloží pomocí useEffect)
  return (
    <div
      ref={containerRef}
      className={cn('inline-flex items-center justify-center', className)}
      style={style}
      aria-label={alt}
      role="img"
    />
  );
}
